import { useServices } from "@/services-context";
import { JsonLdDataset } from "@/services/json-ld";
import { SeoMarkupDataset } from "@/services/seo-markup";
import { createElement, VNode } from "preact";
import { useMemo } from "preact/hooks";
import { Helmet } from "react-helmet-async";

const createJsonLdTag = (key: keyof JsonLdDataset, payload: Object) => {
    return createElement("script", { type: "application/ld+json", key }, JSON.stringify(payload));
};

const useJsonLdTag = (key: keyof JsonLdDataset, dataset: JsonLdDataset = {}) => {
    const { jsonld } = useServices();

    const value = dataset[key];

    return useMemo(() => {
        const payload = jsonld.getScriptPayload(key, dataset);

        return payload
            ? createJsonLdTag(key, payload)
            : null;
    }, [value]);
};

const useSeoMarkup = (dataset?: SeoMarkupDataset) => {
    const { seoMarkup } = useServices();

    return useMemo(() => {
        const tags = [] as VNode<any>[];

        if (!dataset || !seoMarkup.isPrimaryDomain) {
            return tags;
        }

        const links = seoMarkup.getLinks(dataset);

        if (links?.canonical) {
            tags.push(
                createElement("link", {
                    rel: "canonical",
                    key: "canonical",
                    href: links.canonical.url
                })
            );
        }

        for (const alternate of links?.alternates ?? []) {
            tags.push(
                createElement("link", {
                    rel: "alternate",
                    key: `alternate-${alternate.lang}`,
                    href: alternate.url,
                    hreflang: alternate.lang
                })
            );
        }

        const defaultAlt = links?.alternates?.find((a) => a.lang === "en");

        if (defaultAlt) {
            tags.push(
                createElement("link", {
                    rel: "alternate",
                    key: `alternate-x-default`,
                    href: defaultAlt.url,
                    hreflang: "x-default"
                })
            );
        }

        return tags;
    }, [dataset]);
};

export const PageTags = (params: { jsonld?: JsonLdDataset, meta?: { title?: string, description?: string }, seo?: SeoMarkupDataset }) => {
    const pageTag = useJsonLdTag('page', params.jsonld);
    const searchTag = useJsonLdTag('search', params.jsonld);
    const breadcrumbTag = useJsonLdTag('breadcrumbs', params.jsonld);
    const productTag = useJsonLdTag('product', params.jsonld);
    const orgranizationTag = useJsonLdTag('organization', params.jsonld);
    const productListTag = useJsonLdTag('productList', params.jsonld);
    const aggregatedTag = useJsonLdTag('aggregated', params.jsonld);

    const seoTags = useSeoMarkup(params.seo);

    return (
        <Helmet>
            {params.meta?.title && (
                <title>{params.meta.title}</title>
            )}
            {params.meta?.description && (
                <meta name="description" content={params.meta.description} />
            )}
            {pageTag}
            {searchTag}
            {breadcrumbTag}
            {productTag}
            {orgranizationTag}
            {seoTags}
            {productListTag}
            {aggregatedTag}
        </Helmet>
    );
};